/**
 * Collapsible, syntax-coloured JSON tree — the n8n-style JSON lens.
 *
 * REPLACES a `<pre>{JSON.stringify(v, null, 2)}</pre>`, which is unreadable the
 * moment a payload is more than a screenful: no folding, no types at a glance,
 * and a 500-item batch is a wall of text you have to scroll to understand.
 *
 * Deliberately hand-rolled rather than a dep: the shape we render is small
 * (JSON, not arbitrary JS), it must inherit the app's theme tokens, and it's
 * embedded in four surfaces where a heavy tree lib would be all cost.
 */

import * as React from "react";

import { ChevronDown, ChevronRight } from "lucide-react";

import { cn } from "@/lib/cn";

/**
 * How deep to auto-expand.
 *
 * 3 is not arbitrary: Tray's payloads are SERIALISED batches shaped
 * `[ { json: {…}, binary: {}, meta: {} } ]`, so the user's actual data sits at
 * depth 3 (array → item → json → fields). At 2 you'd open the inspector onto a
 * collapsed `json {} 4 campi` — technically a tree, practically useless.
 */
const DEFAULT_EXPAND_DEPTH = 3;

export type JsonKind = "object" | "array" | "string" | "number" | "boolean" | "null";

export function jsonKindOf(v: unknown): JsonKind {
  if (v === null || v === undefined) return "null";
  if (Array.isArray(v)) return "array";
  return typeof v === "object"
    ? "object"
    : (typeof v as "string" | "number" | "boolean");
}

/** The one-line summary shown when a branch is COLLAPSED — it has to carry
 *  enough to decide whether to open it, hence the size, not just "{…}". */
export function collapsedSummary(v: unknown): string {
  if (Array.isArray(v)) return `[] ${v.length} ${v.length === 1 ? "elemento" : "elementi"}`;
  if (v && typeof v === "object") {
    const keys = Object.keys(v as object);
    return `{} ${keys.length} ${keys.length === 1 ? "campo" : "campi"}`;
  }
  return "";
}

function Scalar({ value }: { value: unknown }) {
  const kind = jsonKindOf(value);
  if (kind === "null") return <span className="text-muted-foreground italic">null</span>;
  if (kind === "string") return <span className="text-success">&quot;{String(value)}&quot;</span>;
  if (kind === "number") return <span className="text-info">{String(value)}</span>;
  if (kind === "boolean")
    return <span className="text-warning">{value ? "true" : "false"}</span>;
  return <span>{String(value)}</span>;
}

function Row({
  label,
  value,
  depth,
  isLast,
}: {
  label?: string;
  value: unknown;
  depth: number;
  isLast: boolean;
}) {
  const kind = jsonKindOf(value);
  const branch = kind === "object" || kind === "array";
  const [open, setOpen] = React.useState(depth < DEFAULT_EXPAND_DEPTH);

  const entries: [string, unknown][] = !branch
    ? []
    : Array.isArray(value)
      ? value.map((v, i) => [String(i), v])
      : Object.entries(value as Record<string, unknown>);

  return (
    <div style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
      <div
        className={cn(
          "flex items-start gap-1 rounded px-0.5 font-mono text-[11px] leading-5",
          branch && "cursor-pointer hover:bg-muted/50",
        )}
        onClick={branch ? () => setOpen((o) => !o) : undefined}
      >
        {branch ? (
          open ? (
            <ChevronDown className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" strokeWidth={1.5} />
          ) : (
            <ChevronRight className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" strokeWidth={1.5} />
          )
        ) : (
          // Keep scalars aligned with their branch siblings.
          <span className="w-3 shrink-0" />
        )}
        {label !== undefined && (
          // `accent-foreground` is the colour that sits ON `bg-accent` — it
          // INVERTS per theme by design, so as a bare text colour it renders
          // near-black on the dark bg and white on the light one. Keys are
          // ordinary text: `foreground` is the token that follows the theme.
          <span className="shrink-0 text-foreground">
            {label}
            <span className="text-muted-foreground">:</span>
          </span>
        )}
        {branch ? (
          <span className="text-muted-foreground">
            {open ? (Array.isArray(value) ? "[" : "{") : collapsedSummary(value)}
          </span>
        ) : (
          <span className="min-w-0 break-all">
            <Scalar value={value} />
            {!isLast && <span className="text-muted-foreground">,</span>}
          </span>
        )}
      </div>
      {branch && open && (
        <>
          {entries.map(([k, v], i) => (
            <Row
              key={k}
              label={Array.isArray(value) ? undefined : k}
              value={v}
              depth={depth + 1}
              isLast={i === entries.length - 1}
            />
          ))}
          <div className="pl-4 font-mono text-[11px] leading-5 text-muted-foreground">
            {Array.isArray(value) ? "]" : "}"}
            {!isLast && ","}
          </div>
        </>
      )}
    </div>
  );
}

export function JsonTree({ value, className }: { value: unknown; className?: string }) {
  return (
    <div className={cn("min-w-0", className)}>
      <Row value={value} depth={0} isLast />
    </div>
  );
}
